import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, FileText, Gamepad2, Loader2, Plus, X } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "empresa", label: "Site para Empresa", icon: Building2 },
  { id: "games", label: "Site de Jogo / eSports", icon: Gamepad2 },
  { id: "outro", label: "Outro projeto", icon: FileText },
];

const STATUS_STYLE = {
  novo: { label: "Novo", cls: "border-[#E50914]/40 bg-[#E50914]/10 text-[#FF2A3B]" },
  "em contato": { label: "Em contato", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  fechado: { label: "Fechado", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" },
};

const TYPE_LABEL = { empresa: "Site para Empresa", games: "Site de Jogo / eSports", outro: "Outro projeto" };

const formatDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
  } catch {
    return "";
  }
};

export const QuoteSection = () => {
  const [open, setOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [type, setType] = useState("empresa");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);

  const load = () =>
    fetch(`${API}/quotes/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setQuotes(d.quotes || []))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ project_type: type, description }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Erro ao enviar");
      }
      toast.success("Pedido enviado! O Luiz responde em até 24h.");
      setOpen(false);
      setDescription("");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-[#22222A] bg-[#101014]">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#1A1A20] px-8 py-5">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-[#E50914]" />
          <div>
            <h2 className="font-display text-lg font-bold">Meus pedidos de orçamento</h2>
            <p className="text-xs text-zinc-500">Acompanhe o status das suas solicitações</p>
          </div>
        </div>
        <button
          data-testid="quote-open-modal-btn"
          onClick={() => setOpen(true)}
          className="btn-red-glow flex items-center gap-2 rounded-full bg-[#E50914] px-5 py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
        >
          <Plus className="h-4 w-4" />
          Solicitar orçamento
        </button>
      </div>

      <div className="space-y-3 p-4 sm:p-6" data-testid="quote-list">
        {quotes.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-zinc-600">
            Nenhum pedido ainda. Clique em "Solicitar orçamento" e conte o que você precisa.
          </p>
        ) : (
          quotes.map((q) => (
            <div
              key={q.quote_id}
              data-testid={`quote-item-${q.quote_id.slice(-6)}`}
              className="rounded-xl border border-[#1A1A20] bg-[#0A0A0C] p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono-code text-xs text-zinc-300">{TYPE_LABEL[q.project_type]}</span>
                <span className={`rounded-full border px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest ${STATUS_STYLE[q.status]?.cls}`}>
                  {STATUS_STYLE[q.status]?.label || q.status}
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{q.description}</p>
              <span className="mt-3 block font-mono-code text-[10px] text-zinc-600">
                Enviado em {formatDate(q.created_at)}
              </span>
            </div>
          ))
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="quote-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-[#22222A] bg-[#101014] p-8"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl font-bold text-zinc-100">Solicitar orçamento</h3>
                  <p className="mt-1 text-sm text-zinc-500">Conte o que você precisa — sem compromisso.</p>
                </div>
                <button
                  data-testid="quote-modal-close"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2A2A33] text-zinc-400 transition-colors hover:border-[#E50914] hover:text-white"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`quote-type-${t.id}`}
                    onClick={() => setType(t.id)}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-colors ${
                      type === t.id
                        ? "border-[#E50914] bg-[#E50914]/10 text-white"
                        : "border-[#22222A] text-zinc-400 hover:border-[#E50914]/40"
                    }`}
                  >
                    <t.icon className={`h-5 w-5 ${type === t.id ? "text-[#E50914]" : "text-zinc-500"}`} />
                    <span className="text-xs font-semibold">{t.label}</span>
                  </button>
                ))}
              </div>

              <textarea
                data-testid="quote-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Ex: Preciso de um site para minha loja com página de produtos, WhatsApp e boa posição no Google..."
                className="mt-4 w-full resize-none rounded-xl border border-[#22222A] bg-[#050505] p-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
              />

              <button
                data-testid="quote-submit-btn"
                onClick={submit}
                disabled={sending || description.trim().length < 10}
                className="btn-red-glow mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E50914] font-mono-code text-sm font-bold uppercase tracking-widest text-white disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar pedido"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#101014", border: "1px solid #22222A", color: "#F4F4F6" } }} />
    </div>
  );
};
